import FranchiseAPI from '../../../api/franchises';
import types from '../../mutation-types';

const responsePayload = response => response.data?.payload ?? response.data;
const responseMeta = response => response.data?.meta;

// The index endpoint pages with a fixed size on the server, so anything that has
// to list every franchise walks the pages. Capped so a huge account can't fire
// an unbounded burst of requests.
const ALL_FRANCHISES_MAX_PAGES = 20;

export const actions = {
  get: async ({ commit }, { page = 1, status = '' } = {}) => {
    commit(types.SET_FRANCHISE_UI_FLAG, { isFetching: true });
    try {
      const response = await FranchiseAPI.get(page, status);
      commit(types.CLEAR_FRANCHISES);
      commit(types.SET_FRANCHISES, responsePayload(response));
      commit(types.SET_FRANCHISE_META, responseMeta(response));
    } catch (error) {
      // Keep current state on transient failure
    } finally {
      commit(types.SET_FRANCHISE_UI_FLAG, { isFetching: false });
    }
  },

  // Kept apart from `get` so paginating the franchises list never trims the set
  // a dropdown is showing — a filter that only knows page 1 silently drops the
  // ids it can't see.
  getAll: async ({ commit }) => {
    commit(types.SET_FRANCHISE_UI_FLAG, { isFetching: true });
    try {
      const firstPage = await FranchiseAPI.get(1);
      const records = responsePayload(firstPage) || [];
      const total = responseMeta(firstPage)?.count ?? records.length;
      const pageCount = records.length
        ? Math.min(Math.ceil(total / records.length), ALL_FRANCHISES_MAX_PAGES)
        : 1;
      const pendingPages = [];
      for (let page = 2; page <= pageCount; page += 1) {
        pendingPages.push(FranchiseAPI.get(page));
      }
      const responses = await Promise.all(pendingPages);
      responses.forEach(response => {
        records.push(...(responsePayload(response) || []));
      });
      commit(types.SET_ALL_FRANCHISES, records);
    } catch (error) {
      // Keep current state on transient failure
    } finally {
      commit(types.SET_FRANCHISE_UI_FLAG, { isFetching: false });
    }
  },

  search: async ({ commit }, { search, page = 1, status = '' } = {}) => {
    commit(types.SET_FRANCHISE_UI_FLAG, { isFetching: true });
    try {
      const response = await FranchiseAPI.search(search, page, status);
      commit(types.CLEAR_FRANCHISES);
      commit(types.SET_FRANCHISES, responsePayload(response));
      commit(types.SET_FRANCHISE_META, responseMeta(response));
    } catch (error) {
      // Keep current state on transient failure
    } finally {
      commit(types.SET_FRANCHISE_UI_FLAG, { isFetching: false });
    }
  },

  filter: async (
    { commit },
    { page = 1, queryPayload, resetState = true } = {}
  ) => {
    commit(types.SET_FRANCHISE_UI_FLAG, { isFetching: true });
    try {
      const response = await FranchiseAPI.filter(page, queryPayload);
      if (resetState) {
        commit(types.CLEAR_FRANCHISES);
        commit(types.SET_FRANCHISES, responsePayload(response));
        commit(types.SET_FRANCHISE_META, responseMeta(response));
      }
      return responsePayload(response);
    } finally {
      commit(types.SET_FRANCHISE_UI_FLAG, { isFetching: false });
    }
  },

  show: async ({ commit }, { id }) => {
    commit(types.SET_FRANCHISE_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await FranchiseAPI.show(id);
      commit(types.SET_FRANCHISE_ITEM, responsePayload(response));
      commit(types.SET_CURRENT_FRANCHISE, responsePayload(response));
    } catch (error) {
      // Leave profile state intact
    } finally {
      commit(types.SET_FRANCHISE_UI_FLAG, { isFetchingItem: false });
    }
  },

  create: async ({ commit }, franchiseParams) => {
    commit(types.SET_FRANCHISE_UI_FLAG, { isCreating: true });
    try {
      const response = await FranchiseAPI.create(franchiseParams);
      commit(types.SET_FRANCHISE_ITEM, responsePayload(response));
      return responsePayload(response);
    } finally {
      commit(types.SET_FRANCHISE_UI_FLAG, { isCreating: false });
    }
  },

  update: async ({ commit }, { id, ...franchiseParams }) => {
    commit(types.SET_FRANCHISE_UI_FLAG, { isUpdating: true });
    try {
      const response = await FranchiseAPI.update(id, franchiseParams);
      commit(types.EDIT_FRANCHISE, responsePayload(response));
      return responsePayload(response);
    } finally {
      commit(types.SET_FRANCHISE_UI_FLAG, { isUpdating: false });
    }
  },

  setSuspension: async ({ commit }, { id, suspended, suspensionReason }) => {
    commit(types.SET_FRANCHISE_UI_FLAG, { isUpdating: true });
    try {
      const response = suspended
        ? await FranchiseAPI.suspend(id, suspensionReason)
        : await FranchiseAPI.reactivate(id);
      commit(types.EDIT_FRANCHISE, responsePayload(response));
      return responsePayload(response);
    } finally {
      commit(types.SET_FRANCHISE_UI_FLAG, { isUpdating: false });
    }
  },

  delete: async ({ commit }, id) => {
    commit(types.SET_FRANCHISE_UI_FLAG, { isDeleting: true });
    try {
      await FranchiseAPI.delete(id);
      commit(types.DELETE_FRANCHISE, id);
    } finally {
      commit(types.SET_FRANCHISE_UI_FLAG, { isDeleting: false });
    }
  },

  setSelectedFranchiseIds({ commit }, ids) {
    commit(types.SET_SELECTED_FRANCHISE_IDS, ids);
  },

  clearSelectedFranchiseIds({ commit }) {
    commit(types.CLEAR_SELECTED_FRANCHISE_IDS);
  },

  // Units
  getUnits: async ({ commit }, franchiseId) => {
    commit(types.SET_FRANCHISE_UNITS_UI_FLAG, { isFetching: true });
    try {
      const response = await FranchiseAPI.getUnits(franchiseId);
      commit(types.SET_FRANCHISE_UNITS, responsePayload(response));
    } finally {
      commit(types.SET_FRANCHISE_UNITS_UI_FLAG, { isFetching: false });
    }
  },

  getAllUnits: async ({ commit }) => {
    commit(types.SET_FRANCHISE_UNITS_UI_FLAG, { isFetching: true });
    try {
      const response = await FranchiseAPI.getAllUnits();
      commit(types.SET_FRANCHISE_ALL_UNITS, responsePayload(response));
    } catch (error) {
      // Keep current state on transient failure
    } finally {
      commit(types.SET_FRANCHISE_UNITS_UI_FLAG, { isFetching: false });
    }
  },

  createUnit: async ({ commit }, { franchiseId, payload }) => {
    commit(types.SET_FRANCHISE_UNITS_UI_FLAG, { isCreating: true });
    try {
      const response = await FranchiseAPI.createUnit(franchiseId, payload);
      commit(types.ADD_FRANCHISE_UNIT, responsePayload(response));
      return responsePayload(response);
    } finally {
      commit(types.SET_FRANCHISE_UNITS_UI_FLAG, { isCreating: false });
    }
  },

  updateUnit: async ({ commit }, { franchiseId, unitId, payload }) => {
    commit(types.SET_FRANCHISE_UNITS_UI_FLAG, { isUpdating: true });
    try {
      const response = await FranchiseAPI.updateUnit(
        franchiseId,
        unitId,
        payload
      );
      commit(types.UPDATE_FRANCHISE_UNIT, responsePayload(response));
      return responsePayload(response);
    } finally {
      commit(types.SET_FRANCHISE_UNITS_UI_FLAG, { isUpdating: false });
    }
  },

  deleteUnit: async ({ commit }, { franchiseId, unitId }) => {
    commit(types.SET_FRANCHISE_UNITS_UI_FLAG, { isDeleting: true });
    try {
      await FranchiseAPI.deleteUnit(franchiseId, unitId);
      commit(types.DELETE_FRANCHISE_UNIT, unitId);
    } finally {
      commit(types.SET_FRANCHISE_UNITS_UI_FLAG, { isDeleting: false });
    }
  },

  // Attendants
  getAttendants: async ({ commit }, franchiseId) => {
    commit(types.SET_FRANCHISE_ATTENDANTS_UI_FLAG, { isFetching: true });
    try {
      const response = await FranchiseAPI.getAttendants(franchiseId);
      commit(types.SET_FRANCHISE_ATTENDANTS, responsePayload(response));
    } finally {
      commit(types.SET_FRANCHISE_ATTENDANTS_UI_FLAG, { isFetching: false });
    }
  },

  createAttendant: async ({ commit }, { franchiseId, payload }) => {
    commit(types.SET_FRANCHISE_ATTENDANTS_UI_FLAG, { isCreating: true });
    try {
      const response = await FranchiseAPI.createAttendant(franchiseId, payload);
      commit(types.ADD_FRANCHISE_ATTENDANT, responsePayload(response));
      return responsePayload(response);
    } finally {
      commit(types.SET_FRANCHISE_ATTENDANTS_UI_FLAG, { isCreating: false });
    }
  },

  updateAttendant: async ({ commit }, { franchiseId, userId, payload }) => {
    commit(types.SET_FRANCHISE_ATTENDANTS_UI_FLAG, { isUpdating: true });
    try {
      const response = await FranchiseAPI.updateAttendant(
        franchiseId,
        userId,
        payload
      );
      commit(types.ADD_FRANCHISE_ATTENDANT, responsePayload(response));
      return responsePayload(response);
    } finally {
      commit(types.SET_FRANCHISE_ATTENDANTS_UI_FLAG, { isUpdating: false });
    }
  },

  deleteAttendant: async ({ commit }, { franchiseId, userId }) => {
    commit(types.SET_FRANCHISE_ATTENDANTS_UI_FLAG, { isDeleting: true });
    try {
      await FranchiseAPI.deleteAttendant(franchiseId, userId);
      commit(types.DELETE_FRANCHISE_ATTENDANT, userId);
    } finally {
      commit(types.SET_FRANCHISE_ATTENDANTS_UI_FLAG, { isDeleting: false });
    }
  },
};
